import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Guias";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const ListarGuiasdeAprendizaje = ({ refresh }) => {
  const [guias, setGuias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const { codPrograma } = useParams();
  const navigate = useNavigate();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO GUIAS
  // ==============================
  const getModuloGuias = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloGuias = useMemo(() => getModuloGuias(), []);

  const puedeCrear =
    moduloGuias?.puedeCrear === true ||
    moduloGuias?.crear === true ||
    moduloGuias?.create === true;

  const puedeActualizar =
    moduloGuias?.puedeActualizar === true ||
    moduloGuias?.actualizar === true ||
    moduloGuias?.editar === true ||
    moduloGuias?.update === true;

  const puedeEliminar =
    moduloGuias?.puedeEliminar === true ||
    moduloGuias?.eliminar === true ||
    moduloGuias?.delete === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // GENERAR Y DESCARGAR
  // ==============================
  const generarYDescargarGuia = async (
    idGuia,
    codProgramaValue,
    numeroGuia
  ) => {
    try {
      const response = await axios.get(
        `/api/guia-aprendizaje/generar-docx/${idGuia}`,
        {
          responseType: "blob",
          ...authConfig(),
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute(
        "download",
        `GuiaAprendizaje_${codProgramaValue}_N${numeroGuia}.docx`
      );

      document.body.appendChild(link);

      link.click();

      link.remove();
    } catch (error) {
      console.error("Error al generar la guía:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo generar la guía.", "error");
    }
  };

  // ==============================
  // ELIMINAR GUÍA
  // ==============================
  const deleteGuia = useCallback(
    async (idGuia) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar guías.",
          "warning"
        );

        return;
      }

      try {
        const confirm = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡Esta acción no se puede deshacer!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        setDeletingId(idGuia);

        const { data } = await axios.delete(
          `/api/programas/guias/delete/${idGuia}`,
          authConfig()
        );

        setGuias((prev) => prev.filter((g) => g._id !== idGuia));

        Swal.fire({
          icon: "success",
          title: data.message || "Guía eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar la guía:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire("Error", "No se pudo eliminar la guía.", "error");
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // ==============================
  // OBTENER DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `/api/programas/guias/listidprograma/${codPrograma}`,
        authConfig()
      );

      if (
        response.data &&
        response.data.ok &&
        Array.isArray(response.data.guias)
      ) {
        const guiasOrdenadas = response.data.guias.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        const total = guiasOrdenadas.length;

        const guiasConFunciones = guiasOrdenadas.map((guia, index) => {
          const numeroGuia = total - index;

          return {
            ...guia,
            numeroGuia,

            funciones: (
              <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">
                {/* Descargar */}
                <button
                  className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() =>
                    generarYDescargarGuia(
                      guia._id,
                      guia.codPrograma,
                      numeroGuia
                    )
                  }
                  title={`Descargar Guía N° ${numeroGuia}`}
                  type="button"
                >
                  <i className="fa-solid fa-file-word"></i>
                </button>

                {/* Editar */}
                {puedeActualizar && (
                  <button
                    className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() =>
                      navigate(`/institucional/guias/editar/${guia._id}`)
                    }
                    title="Editar guía"
                    type="button"
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </button>
                )}

                {/* Eliminar */}
                {puedeEliminar && (
                  <button
                    className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() => deleteGuia(guia._id)}
                    title="Eliminar guía"
                    type="button"
                    disabled={deletingId === guia._id}
                  >
                    <i
                      className={`fa-solid ${
                        deletingId === guia._id
                          ? "fa-spinner fa-spin"
                          : "fa-trash"
                      }`}
                    ></i>
                  </button>
                )}
              </div>
            ),
          };
        });

        setGuias(guiasConFunciones);
      } else {
        setGuias([]);
      }
    } catch (error) {
      console.error("Error al cargar las guías:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudieron cargar las guías.", "error");

      setGuias([]);
    } finally {
      setLoading(false);
    }
  }, [
    codPrograma,
    navigate,
    deleteGuia,
    manejarErrorSesion,
    puedeActualizar,
    puedeEliminar,
  ]);

  useEffect(() => {
    if (codPrograma) getData();
  }, [codPrograma, getData, refresh]);

  // ==============================
  // COLUMNAS
  // ==============================
  const columns = useMemo(
    () => [
      { Header: "N° Guía", accessor: "numeroGuia" },
      { Header: "Programa", accessor: "nombrePrograma" },
      { Header: "Proyecto Formativo", accessor: "nombreProyectoFormativo" },
      { Header: "Fase", accessor: "faseProyecto" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  // ==============================
  // RENDER
  // ==============================
  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Guías de aprendizaje
                </h5>

                <small className="text-secondary">
                  Gestión de guías por programa: edición, descarga y
                  eliminación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => navigate("/institucional/programas")}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => getData()}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(`/institucional/guias/nuevo/${codPrograma}`)
                    }
                    disabled={loading || !codPrograma}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando guías…</div>
              </div>
            ) : Array.isArray(guias) && guias.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={guias} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📄</div>

                <div className="fw-semibold">
                  No hay guías disponibles
                </div>

                <div className="small">
                  No se encontraron guías para este programa.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark mt-3"
                    type="button"
                    onClick={() =>
                      navigate(`/institucional/guias/nuevo/${codPrograma}`)
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar guía
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarGuiasdeAprendizaje;